/**
 * API Service - Axios Configuration
 * 
 * Centralized API client for all backend communication.
 * Because making API calls without a proper setup is for amateurs! 😎
 */

import axios from 'axios';

// Get base URL from environment variables
// For health checks: use root URL
// For API calls: use /api/v1
const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8002/api/v1';
const ROOT_URL = import.meta.env.VITE_ROOT_URL || 'http://localhost:8002'; // For health checks
const TIMEOUT = import.meta.env.VITE_API_TIMEOUT || 30000;
const SLOW_REQUEST_THRESHOLD = Number(
  import.meta.env.VITE_SLOW_API_THRESHOLD || 4000,
);

// Flag to suppress 401 errors during logout transition
let isLoggingOut = false;
let requestSequence = 0;
let apiMonitoringEnabled = true;

const apiMonitorListeners = new Set();
const activeApiRequests = new Map();
let recentIssues = [];
let cachedApiMonitorSnapshot = {
  activeRequests: [],
  recentIssues: [],
};

const emitApiMonitorUpdate = () => {
  cachedApiMonitorSnapshot = {
    activeRequests: Array.from(activeApiRequests.values()).map((request) => ({
      requestId: request.requestId,
      url: request.url,
      method: request.method,
      label: request.label,
      startedAt: request.startedAt,
      isSlow: request.isSlow,
      slowAfterMs: request.slowAfterMs,
      slowSince: request.slowSince,
    })),
    recentIssues,
  };
  apiMonitorListeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.error("API monitor listener failed:", error);
    }
  });
};

const sanitizeUrl = (url = '') => url.split('?')[0];

const buildRequestLabel = (config = {}) => {
  const configuredLabel = config.apiMonitorLabel || config.meta?.apiMonitorLabel;
  if (configuredLabel) {
    return configuredLabel;
  }

  const url = sanitizeUrl(config.url || '');
  if (!url) {
    return 'API request';
  }

  return url
    .replace(/^\/+/, '')
    .split('/')
    .filter(Boolean)
    .slice(-2)
    .join(' / ');
};

const upsertActiveRequest = (requestId, patch) => {
  const existing = activeApiRequests.get(requestId);
  if (!existing) {
    return;
  }

  activeApiRequests.set(requestId, {
    ...existing,
    ...patch,
  });
  emitApiMonitorUpdate();
};

const addRecentIssue = (issue) => {
  recentIssues = [
    issue,
    ...recentIssues.filter((entry) => entry.requestId !== issue.requestId),
  ].slice(0, 20);
  emitApiMonitorUpdate();
};

const clearRecentIssue = (requestId) => {
  const nextIssues = recentIssues.filter((issue) => issue.requestId !== requestId);
  if (nextIssues.length !== recentIssues.length) {
    recentIssues = nextIssues;
    emitApiMonitorUpdate();
  }
};

const startMonitoringRequest = (config) => {
  if (!apiMonitoringEnabled) {
    return config;
  }

  const requestId = `api-${Date.now()}-${requestSequence += 1}`;
  const startedAt = Date.now();
  const slowAfterMs =
    config.slowRequestThresholdMs ||
    config.meta?.slowRequestThresholdMs ||
    SLOW_REQUEST_THRESHOLD;

  const requestEntry = {
    requestId,
    url: sanitizeUrl(config.url),
    method: (config.method || 'get').toUpperCase(),
    label: buildRequestLabel(config),
    startedAt,
    isSlow: false,
    slowAfterMs,
    timerId: window.setTimeout(() => {
      upsertActiveRequest(requestId, {
        isSlow: true,
        slowSince: Date.now(),
      });
      addRecentIssue({
        requestId,
        kind: 'slow',
        label: buildRequestLabel(config),
        url: sanitizeUrl(config.url),
        method: (config.method || 'get').toUpperCase(),
        startedAt,
        detectedAt: Date.now(),
      });
    }, slowAfterMs),
  };

  activeApiRequests.set(requestId, requestEntry);
  emitApiMonitorUpdate();
  config.__apiMonitorRequestId = requestId;
  return config;
};

const finishMonitoringRequest = (config, extraIssue = null) => {
  if (!apiMonitoringEnabled) {
    return;
  }

  const requestId = config?.__apiMonitorRequestId;
  if (!requestId) {
    return;
  }

  const existing = activeApiRequests.get(requestId);
  if (existing?.timerId) {
    window.clearTimeout(existing.timerId);
  }

  activeApiRequests.delete(requestId);

  if (extraIssue) {
    addRecentIssue({
      requestId,
      label: existing?.label || buildRequestLabel(config),
      url: existing?.url || sanitizeUrl(config?.url),
      method: existing?.method || (config?.method || 'get').toUpperCase(),
      startedAt: existing?.startedAt || Date.now(),
      detectedAt: Date.now(),
      ...extraIssue,
    });
  } else {
    clearRecentIssue(requestId);
  }

  emitApiMonitorUpdate();
};

setInterval(() => {
  const now = Date.now();
  const nextIssues = recentIssues.filter((issue) => {
    if (issue.kind === 'slow') {
      return now - issue.detectedAt < 30000;
    }

    return now - issue.detectedAt < 15000;
  });

  if (nextIssues.length !== recentIssues.length) {
    recentIssues = nextIssues;
    emitApiMonitorUpdate();
  }
}, 5000);

// Create axios instance with default config (for API endpoints)
const api = axios.create({
  baseURL: BASE_URL,
  timeout: TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create separate instance for root-level endpoints (health, ready, alive)
const rootApi = axios.create({
  baseURL: ROOT_URL,
  timeout: TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================================================
// REQUEST INTERCEPTOR - Add auth token to every request 🔐
// ============================================================================
const requestInterceptor = (config) => {
  try {
    const token = localStorage.getItem('access_token');
    config.headers = config.headers || {};

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (import.meta.env.DEV) {
      console.log(
        '🚀 API Request:',
        (config.method || 'get').toUpperCase(),
        config.url,
      );
    }

    return startMonitoringRequest(config);
  } catch (error) {
    apiMonitoringEnabled = false;
    console.error('API request monitor setup failed:', error);
    return config;
  }
};

const requestErrorHandler = (error) => {
  console.error('❌ Request Error:', error);
  return Promise.reject(error);
};

// Apply interceptor to both instances
api.interceptors.request.use(requestInterceptor, requestErrorHandler);
rootApi.interceptors.request.use(requestInterceptor, requestErrorHandler);

// ============================================================================
// RESPONSE INTERCEPTOR - Handle responses and errors 🎯
// ============================================================================
const responseSuccessHandler = (response) => {
  // Log response in development
  if (import.meta.env.DEV) {
    console.log('✅ API Response:', response.status, response.config.url);
  }

  try {
    finishMonitoringRequest(response.config);
  } catch (error) {
    apiMonitoringEnabled = false;
    console.error('API request monitor cleanup failed:', error);
  }
  return response;
};

const responseErrorHandler = async (error) => {
  const originalRequest = error.config;
  
  // If we are already logging out, just silence the error and reject
  if (isLoggingOut) {
    try {
      finishMonitoringRequest(originalRequest, {
        kind: 'failed',
        status: error.response?.status,
        message: error.message || 'Request failed.',
      });
    } catch (monitorError) {
      apiMonitoringEnabled = false;
      console.error('API request monitor cleanup failed:', monitorError);
    }
    return Promise.reject(error);
  }

  // Define error types for easier handling in the UI
  const errorInfo = {
    message: error.message || 'An unexpected error occurred.',
    status: error.response?.status,
    url: error.config?.url,
    type: 'UNKNOWN',
    isTimeout: error.code === 'ECONNABORTED',
    isServerError: error.response?.status >= 500,
    isNetworkError: !error.response && error.request,
  };

  if (errorInfo.isTimeout) errorInfo.type = 'TIMEOUT';
  else if (errorInfo.isServerError) errorInfo.type = 'SERVER_ERROR';
  else if (errorInfo.isNetworkError) errorInfo.type = 'NETWORK_ERROR';

  // Handle 401 Unauthorized - Token expired
  if (error.response?.status === 401 && !originalRequest._retry) {
    originalRequest._retry = true;
    
    try {
      // Try to refresh token
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (refreshToken) {
        const response = await axios.post(
          `${BASE_URL}/auth/token/refresh/`,
          { refresh: refreshToken }
        );
        
        const { access } = response.data;
        
        // Save new token
        localStorage.setItem('access_token', access);
        
        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return api(originalRequest);
      }
    } catch (refreshError) {
      // Refresh failed - logout user
      console.error('❌ Token refresh failed:', refreshError);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      
      // Redirect to login (we'll implement this later)
      window.location.href = '/login';
      try {
        finishMonitoringRequest(originalRequest, {
          kind: 'failed',
          status: refreshError.response?.status,
          message: refreshError.message || 'Token refresh failed.',
        });
      } catch (monitorError) {
        apiMonitoringEnabled = false;
        console.error('API request monitor cleanup failed:', monitorError);
      }
      return Promise.reject(refreshError);
    }
  }
  
  // Log error in development
  if (import.meta.env.DEV) {
    console.error(`❌ API [${errorInfo.type}] Error:`, errorInfo.status || 'No Status', errorInfo.url);
    if (error.response?.data) {
      console.error('Error details:', error.response.data);
    }
  }
  
  // Attach the structured info to the error and reject
  error.info = errorInfo;
  try {
    finishMonitoringRequest(originalRequest, {
      kind: 'failed',
      status: errorInfo.status,
      message: errorInfo.message,
      type: errorInfo.type,
    });
  } catch (monitorError) {
    apiMonitoringEnabled = false;
    console.error('API request monitor cleanup failed:', monitorError);
  }
  return Promise.reject(error);
};

// Apply interceptor to both instances
api.interceptors.response.use(responseSuccessHandler, responseErrorHandler);
rootApi.interceptors.response.use(responseSuccessHandler, responseErrorHandler);

// ============================================================================
// EXPORT
// ============================================================================
export default api;
export { rootApi }; // Export rootApi for health checks

export const subscribeToApiMonitor = (listener) => {
  apiMonitorListeners.add(listener);
  return () => {
    apiMonitorListeners.delete(listener);
  };
};

export const getApiMonitorSnapshot = () => cachedApiMonitorSnapshot;

// Helper function to set auth token
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('access_token', token);
  } else {
    localStorage.removeItem('access_token');
  }
};

// Helper function to get auth token
export const getAuthToken = () => {
  return localStorage.getItem('access_token');
};

// Helper function to clear auth data
export const clearAuthData = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
};

// Helper function to set logging out state
export const setLoggingOut = (value) => {
  isLoggingOut = value;
};
